/**
 * GuapoNudge - Dashboard widget for partially onboarded users
 * Shows when user has < 5 bills, encouraging them to complete setup
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '@/utils/api';
import { usePremiumAction, PREMIUM_ACTIONS } from '@/contexts/PremiumActionContext';
import { useSubscription } from '@/contexts/SubscriptionContext';

// Guapo mascot image
const GUAPO_MASCOT = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

export function GuapoNudge({ user, onDismiss }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { queuePremiumAction } = usePremiumAction();
  const { canAccessFeature } = useSubscription();

  useEffect(() => {
    // Check if user needs the nudge
    const checkNudgeStatus = async () => {
      try {
        const response = await apiGet('/auth/me');
        if (response.ok) {
          const data = await response.json();
          // Show nudge if user has partial onboarding and < 5 bills
          if (data.needs_guapo_nudge && !dismissed) {
            setVisible(true);
          }
        }
      } catch (error) {
        console.error('Error checking nudge status:', error);
      }
    };
    
    checkNudgeStatus();
  }, [dismissed]);

  const handleAddBills = () => {
    navigate('/app/bills?mode=add');
  };

  // Navigate directly to Subscription Hunter flow (bypasses intro screens)
  const handleLinkBank = () => {
    // Use the same premium action queue as the Subscription Hunter button
    queuePremiumAction(
      PREMIUM_ACTIONS.SUBSCRIPTION_HUNT,
      () => {
        // Navigate to bills onboarding with plaid mode
        navigate('/app/bills?mode=subscription-hunter');
      },
      { source: 'guapo_nudge' }
    );
  };

  const handleDismiss = async () => {
    setDismissed(true);
    setVisible(false);
    
    // Update backend to not show again for 24 hours
    try {
      await apiPost('/auth/dismiss-guapo-nudge');
    } catch (error) {
      console.error('Error dismissing nudge:', error);
    }
    
    if (onDismiss) onDismiss();
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        className="mb-6"
      >
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-1">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-400/50 via-orange-400/50 to-red-400/50 animate-pulse" />
          
          <div className="relative bg-slate-900 rounded-xl p-4">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 text-white/40 hover:text-white/70 text-xl"
              aria-label="Dismiss"
            >
              ✕
            </button>
            
            <div className="flex items-start gap-4">
              {/* Guapo Mascot Avatar */}
              <div className="relative flex-shrink-0">
                <img 
                  src={GUAPO_MASCOT}
                  alt="Guapo"
                  className="w-14 h-14 rounded-full shadow-lg shadow-emerald-500/30 border-2 border-emerald-400"
                />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
                  <span className="text-[8px] text-white">AI</span>
                </div>
              </div>
              
              {/* Message */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-emerald-400 font-bold text-sm">Guapo</span>
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                    Setup Assistant
                  </span>
                </div>
                
                <p className="text-white text-sm leading-relaxed mb-4">
                  Let's get your money right! Your budget is a blank slate right now. 
                  You can add your bills manually, or securely link your bank to let Subscription Hunter do the work. 💸
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={handleAddBills}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    ✏️ Add Bills Manually
                  </button>
                  <button
                    onClick={handleLinkBank}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-bold rounded-lg hover:shadow-lg hover:shadow-emerald-500/30 transition-all flex items-center gap-2"
                  >
                    🎯 Start the Hunt
                  </button>
                </div>
                
                {/* Guided Tour Link */}
                <button
                  onClick={() => navigate('/app/onboarding')}
                  className="text-white/60 hover:text-white text-xs underline underline-offset-2 transition-colors"
                >
                  Complete your setup to unlock all features →
                </button>
              </div>
            </div>
            
            {/* Progress hint */}
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center justify-center text-xs">
                <span className="text-gray-400">
                  Next Milestone:
                </span>
                <span className="text-amber-400 font-bold ml-2">
                  📄 Paper Chaser rank awaits!
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compact version for sidebar/nav
export function GuapoNudgeCompact({ onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full p-3 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl text-left hover:border-emerald-500/50 transition-all"
    >
      <div className="flex items-center gap-3">
        <img src={GUAPO_MASCOT} alt="Guapo" className="w-8 h-8 rounded-full" />
        <div className="flex-1 min-w-0">
          <p className="text-emerald-400 text-xs font-bold">Guapo says:</p>
          <p className="text-white/80 text-xs truncate">
            Let's get your money right! 💸
          </p>
        </div>
        <span className="text-emerald-400">→</span>
      </div>
    </motion.button>
  );
}

export default GuapoNudge;
